import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

export default function LoginPage() {
  const { t } = useLang();
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    if (user && typeof user === "object") {
      nav(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    }
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error);
      toast.error(res.error);
    } else {
      toast.success(t("auth.login.welcome"));
      nav(res.user.role === "admin" ? "/admin" : "/dashboard");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-14 bg-gradient-to-br from-[#121418] to-[#0a0b0e] border-r border-white/8">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#d4af37] to-[#a3852c]">
            <Zap className="w-4 h-4 text-[#0a0b0e]" strokeWidth={3} />
          </div>
          <span className="font-display font-semibold text-white text-lg">LoftPress</span>
        </Link>
        <div className="max-w-md">
          <h2 className="font-display text-4xl text-white/95 leading-tight">
            {t("hero.title.a")}<br />
            <span className="text-white/60">{t("hero.title.b")}</span><br />
            <span className="lp-gold-text">{t("hero.title.c")}</span>
          </h2>
        </div>
        <div className="text-xs text-white/40 font-mono">© LoftPress · loftpress.shop</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-14">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md lp-card p-8"
        >
          <h1 className="font-display text-3xl text-white/95">{t("auth.login.title")}</h1>
          <p className="text-white/50 mt-2 text-sm">{t("auth.login.subtitle")}</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/75 mb-2 block font-semibold">{t("auth.email")}</label>
              <input
                type="email"
                required
                autoFocus
                autoComplete="email"
                className="lp-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/75 mb-2 block font-semibold">{t("auth.password")}</label>
              <input
                type="password"
                required
                autoComplete="current-password"
                className="lp-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                placeholder="••••••••"
              />
            </div>
            {err && <div className="text-red-400 text-sm" data-testid="login-error">{err}</div>}
            <button type="submit" disabled={busy} className="lp-btn-primary w-full justify-center" data-testid="login-submit">
              {busy ? t("common.loading") : t("auth.submit.login")} <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between text-sm">
            <Link to="/forgot-password" className="text-[#d4af37] hover:underline" data-testid="login-forgot-link">
              {t("auth.forgot")}
            </Link>
            <div className="text-white/50">
              {t("auth.no")}{" "}
              <Link to="/register" className="text-white hover:text-[#d4af37]" data-testid="login-to-register">
                {t("auth.signUp")}
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
